import { useMaximize } from "./hooks/useMaximize";
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";

export default function Report({ onClose, storeSettings }) {
  const [sales,         setSales]         = useState([]);
  const [cashiers,      setCashiers]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [expanded,      setExpanded]      = useState(null);
  const [saleItems,     setSaleItems]     = useState({});
  const [printing,      setPrinting]      = useState(null);
  const [filter,        setFilter]        = useState("today");
  const [customFrom,    setCustomFrom]    = useState("");
  const [customTo,      setCustomTo]      = useState("");
  const [cashierFilter, setCashierFilter] = useState("all");
  const { toggle, overlayClass, panelClass, icon } = useMaximize(true);
  const detailRef = useRef(null);

  useEffect(() => { fetchCashiers(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSales(); }, [filter, customFrom, customTo, cashierFilter]);

  // Rendered as its own block below the table (not inside it — see the
  // "Expanded detail" section below), so it needs its own scroll-into-view
  // rather than appearing naturally adjacent to the tapped row.
  useEffect(() => {
    if (expanded) detailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [expanded]);

  async function fetchCashiers() {
    const { data, error } = await supabase.from("profiles").select("id, full_name").order("full_name");
    logSupabaseError("Report: fetch cashiers (profiles)", error);
    if (data) setCashiers(data);
  }

  async function fetchSales() {
    setLoading(true);
    setSales([]);
    let query = supabase
      .from("sales_transactions")
      .select(`id, receipt_number, subtotal, tax_amount, total_amount, payment_method, created_at, cashier_id,
        cashier:profiles!sales_transactions_cashier_id_fkey(full_name)`)
      .order("created_at", { ascending: false })
      .limit(200);

    if (filter !== "all" && filter !== "custom") {
      const from = new Date();
      if      (filter === "today") from.setUTCHours(0,0,0,0);
      else if (filter === "week")  from.setUTCDate(from.getUTCDate() - 7);
      else if (filter === "month") from.setUTCDate(from.getUTCDate() - 30);
      query = query.gte("created_at", from.toISOString());
    }
    if (filter === "custom" && customFrom) query = query.gte("created_at", new Date(customFrom).toISOString());
    if (filter === "custom" && customTo)   { const t = new Date(customTo); t.setHours(23,59,59); query = query.lte("created_at", t.toISOString()); }
    if (cashierFilter !== "all")           query = query.eq("cashier_id", cashierFilter);

    const { data, error } = await query;
    logSupabaseError("Report: fetch sales_transactions", error);
    if (data) setSales(data);
    setLoading(false);
  }

  async function fetchItems(txId) {
    if (saleItems[txId]) return;
    const { data, error } = await supabase.from("sale_items").select("*").eq("transaction_id", txId);
    logSupabaseError("Report: fetch sale_items for receipt", error);
    if (data) setSaleItems((prev) => ({ ...prev, [txId]: data }));
  }

  function toggleExpand(id) {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id); fetchItems(id);
  }

  async function handlePrint(sale) {
    await fetchItems(sale.id);
    setPrinting(sale);
    setTimeout(() => window.print(), 400);
  }

  const total      = sales.reduce((s, t) => s + Number(t.total_amount), 0);
  const count      = sales.length;
  const avgBasket  = count ? total / count : 0;

  const payBreakdown = sales.reduce((acc, s) => {
    const m = s.payment_method ?? "cash";
    acc[m]  = { amount: (acc[m]?.amount || 0) + Number(s.total_amount), count: (acc[m]?.count || 0) + 1 };
    return acc;
  }, {});

  const cashierMap = sales.reduce((acc, s) => {
    const name = s.cashier?.full_name ?? "Unknown";
    if (!acc[name]) acc[name] = { amount: 0, count: 0 };
    acc[name].amount += Number(s.total_amount);
    acc[name].count  += 1;
    return acc;
  }, {});

  const payIcons = { cash: "💵", telebirr: "📱", bank: "🏦", card: "💳", other: "🔄" };
  const expandedSale = sales.find((s) => s.id === expanded) ?? null;
  const money = (v) => `ETB ${Number(v ?? 0).toFixed(2)}`;

  return (
    <div className={overlayClass}>
      <div className={panelClass} style={{ maxWidth: "980px" }}>
      {/* Print Receipt */}
      {printing && (
        <div className="print-only">
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <h2 style={{ fontSize: "18px", margin: 0 }}>{storeSettings?.store_name || "MiniMart POS"}</h2>
            {storeSettings?.store_address && (
              <p style={{ fontSize: "12px", margin: "2px 0" }}>{storeSettings.store_address}</p>
            )}
            {storeSettings?.store_phone && (
              <p style={{ fontSize: "12px", margin: "2px 0" }}>Tel: {storeSettings.store_phone}</p>
            )}
            {storeSettings?.store_tin && (
              <p style={{ fontSize: "12px", margin: "2px 0" }}>TIN: {storeSettings.store_tin}</p>
            )}
            <hr style={{ margin: "10px 0" }} />
            <p style={{ fontSize: "11px", color: "#666" }}>Official Receipt</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px", fontSize:"13px", marginBottom:"12px" }}>
            <div><strong>Receipt:</strong></div><div>{printing.receipt_number}</div>
            <div><strong>Cashier:</strong></div><div>{printing.cashier?.full_name ?? "—"}</div>
            <div><strong>Date:</strong></div><div>{new Date(printing.created_at).toLocaleDateString()}</div>
            <div><strong>Time:</strong></div><div>{new Date(printing.created_at).toLocaleTimeString()}</div>
            <div><strong>Payment:</strong></div><div>{printing.payment_method?.toUpperCase()}</div>
          </div>
          <hr style={{ margin:"12px 0" }} />
          <table style={{ width:"100%", fontSize:"13px", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:"1px solid #ccc" }}>
                <th style={{ textAlign:"left",    padding:"4px 0" }}>Item</th>
                <th style={{ textAlign:"center",  padding:"4px 0" }}>Qty</th>
                <th style={{ textAlign:"right",   padding:"4px 0" }}>Price</th>
                <th style={{ textAlign:"right",   padding:"4px 0" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(saleItems[printing.id] || []).map((item) => (
                <tr key={item.id} style={{ borderBottom:"1px solid #eee" }}>
                  <td style={{ padding:"5px 0" }}>{item.product_name}</td>
                  <td style={{ textAlign:"center", padding:"5px 0" }}>{item.quantity}</td>
                  <td style={{ textAlign:"right",  padding:"5px 0" }}>ETB {Number(item.unit_price).toFixed(2)}</td>
                  <td style={{ textAlign:"right",  padding:"5px 0" }}>ETB {Number(item.line_total).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <hr style={{ margin:"12px 0" }} />
          {(() => {
            const vatEnabled = storeSettings?.vat_enabled === "true";
            const vatRate = Number(storeSettings?.vat_rate ?? 0);
            const totalInclVat = Number(printing.total_amount ?? 0);
            const taxAmount = Number(printing.tax_amount ?? 0);
            const subtotal = Number(
              printing.subtotal ?? (vatEnabled ? totalInclVat - taxAmount : totalInclVat)
            );

            const money = (v) => `ETB ${Number(v ?? 0).toFixed(2)}`;

            return (
              <div style={{ display: "grid", rowGap: "6px", fontSize: "13px" }}>
                {vatEnabled && (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: "16px" }}>
                      <div style={{ textAlign: "left" }}>Subtotal</div>
                      <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(subtotal)}</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: "16px" }}>
                      <div style={{ textAlign: "left" }}>VAT Rate</div>
                      <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {Number.isFinite(vatRate) ? `${vatRate}%` : "—"}
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: "16px" }}>
                      <div style={{ textAlign: "left" }}>VAT Amount</div>
                      <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(taxAmount)}</div>
                    </div>
                  </>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    columnGap: "16px",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  <div style={{ textAlign: "left" }}>{vatEnabled ? "Total (Incl. VAT)" : "TOTAL"}</div>
                  <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(totalInclVat)}</div>
                </div>
              </div>
            );
          })()}
          <p style={{ textAlign:"center", fontSize:"11px", marginTop:"20px", color:"#666" }}>
            Thank you for shopping at Guadaye Mini Market! {/* 👈 CHANGE THIS TOO */}
          </p>
        </div>
      )}

      {/* Report UI */}
          {/* Header */}
          <div className="report-header">
            <div>
              <h2 className="report-title">📊 Sales Report</h2>
              <p className="report-date">
                {new Date().toLocaleDateString("en-US", { weekday:"long", year:"numeric", month:"long", day:"numeric" })}
              </p>
            </div>
            {/* Maximize button */}
            <button className="maximize-btn" onClick={toggle} title={icon === "⊞" ? "Maximize" : "Restore"}>
              {icon}
            </button>
            <button className="report-close" onClick={onClose}>✕</button>
          </div>

          {/* Filters */}
          <div className="rpt-filters">
            <div className="rpt-filter-group">
              <label className="rpt-filter-label">📅 PERIOD</label>
              <div className="filter-tabs">
                {[{key:"today",label:"Today"},{key:"week",label:"7 Days"},{key:"month",label:"30 Days"},{key:"all",label:"All Time"},{key:"custom",label:"Custom"}].map((f)=>(
                  <button key={f.key} className={`filter-tab ${filter===f.key?"active":""}`} onClick={()=>setFilter(f.key)}>{f.label}</button>
                ))}
              </div>
            </div>
            <div className="rpt-filter-group">
              <label className="rpt-filter-label">👤 CASHIER</label>
              <select className="cashier-select" value={cashierFilter} onChange={(e)=>setCashierFilter(e.target.value)}>
                <option value="all">All Cashiers</option>
                {cashiers.map((c)=>(<option key={c.id} value={c.id}>{c.full_name}</option>))}
              </select>
            </div>
          </div>

          {/* Custom date range */}
          {filter === "custom" && (
            <div className="custom-dates">
              <div className="date-field"><label>From</label><input type="date" value={customFrom} onChange={(e)=>setCustomFrom(e.target.value)} /></div>
              <div className="date-field"><label>To</label>  <input type="date" value={customTo}   onChange={(e)=>setCustomTo(e.target.value)}   /></div>
            </div>
          )}

          {/* KPIs */}
          <div className="rpt-kpi-row">
            <div className="rpt-kpi cyan">
              <p className="rpt-kpi-icon">💰</p>
              <p className="rpt-kpi-label">Total Revenue</p>
              <p className="rpt-kpi-value">ETB {total.toLocaleString("en",{minimumFractionDigits:2})}</p>
            </div>
            <div className="rpt-kpi amber">
              <p className="rpt-kpi-icon">🧾</p>
              <p className="rpt-kpi-label">Transactions</p>
              <p className="rpt-kpi-value">{count}</p>
            </div>
            <div className="rpt-kpi green">
              <p className="rpt-kpi-icon">🛒</p>
              <p className="rpt-kpi-label">Avg. Basket</p>
              <p className="rpt-kpi-value">ETB {avgBasket.toFixed(2)}</p>
            </div>
          </div>

          {/* Breakdown panels */}
          <div className="rpt-breakdown-grid">
            {/* Payment Methods */}
            <div className="rpt-breakdown-panel">
              <p className="rpt-panel-title">💳 Payment Methods</p>
              {Object.keys(payBreakdown).length === 0
                ? <p className="rpt-empty-small">No data</p>
                : Object.entries(payBreakdown).map(([method, d]) => (
                  <div key={method} className="rpt-breakdown-item">
                    <div className="rpt-breakdown-left">
                      <span className="rpt-breakdown-icon">{payIcons[method] ?? "💳"}</span>
                      <span className="rpt-breakdown-name">{method.toUpperCase()}</span>
                      <span className="rpt-breakdown-count">{d.count} sales</span>
                    </div>
                    <span className="rpt-breakdown-amount">ETB {d.amount.toLocaleString("en",{minimumFractionDigits:2})}</span>
                  </div>
                ))
              }
            </div>

            {/* Cashier Performance */}
            <div className="rpt-breakdown-panel">
              <p className="rpt-panel-title">👤 Cashier Performance</p>
              {Object.keys(cashierMap).length === 0
                ? <p className="rpt-empty-small">No data</p>
                : Object.entries(cashierMap).sort((a,b)=>b[1].amount-a[1].amount).map(([name, d]) => (
                  <div key={name} className="rpt-breakdown-item">
                    <div className="rpt-breakdown-left">
                      <span className="rpt-breakdown-icon">👤</span>
                      <span className="rpt-breakdown-name">{name}</span>
                      <span className="rpt-breakdown-count">{d.count} sales</span>
                    </div>
                    <span className="rpt-breakdown-amount">ETB {d.amount.toLocaleString("en",{minimumFractionDigits:2})}</span>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Transactions */}
          <div className="report-table-wrap">
            {loading ? (
              <p className="report-loading">Loading...</p>
            ) : sales.length === 0 ? (
              <div className="report-empty"><p>🧾</p><p>No sales for this period</p></div>
            ) : (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Receipt #</th>
                    <th>Cashier</th>
                    <th>Payment</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className={expanded===sale.id?"expanded-row":""} style={{cursor:"pointer"}} onClick={()=>toggleExpand(sale.id)}>
                      <td className="receipt-no">{sale.receipt_number}</td>
                      <td style={{color:"var(--text)"}}>{sale.cashier?.full_name ?? "—"}</td>
                      <td>
                        <span className="payment-badge">
                          {payIcons[sale.payment_method]??""} {sale.payment_method?.toUpperCase()}
                        </span>
                      </td>
                      <td className="time-col">{new Date(sale.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</td>
                      <td className="time-col">{new Date(sale.created_at).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}</td>
                      <td className="amount-col">ETB {Number(sale.total_amount).toFixed(2)}</td>
                      <td onClick={(e)=>e.stopPropagation()}>
                        <button className="print-btn" onClick={()=>handlePrint(sale)} title="Print">🖨️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="5" className="total-label">TOTAL ({count} transactions)</td>
                    <td className="total-amount">ETB {total.toLocaleString("en",{minimumFractionDigits:2})}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Expanded detail — deliberately rendered OUTSIDE .report-table-wrap
              (not nested inside a colSpan cell of the table) so it never
              inherits the table's horizontal scroll position or 600px width.
              See PROJECT_STATE.md for the diagnosis this replaced. */}
          {expandedSale && (() => {
            const vatEnabled = storeSettings?.vat_enabled === "true";
            const vatRate = Number(storeSettings?.vat_rate ?? 0);
            const totalInclVat = Number(expandedSale.total_amount ?? 0);
            const taxAmount = Number(expandedSale.tax_amount ?? 0);
            const subtotal = Number(
              expandedSale.subtotal ?? (vatEnabled ? totalInclVat - taxAmount : totalInclVat)
            );
            const items = saleItems[expandedSale.id];

            return (
              <div className="rpt-expanded-detail" ref={detailRef}>
                <div className="rpt-expanded-header">
                  <div>
                    <p className="rpt-expanded-receipt">🛒 Items Sold — {expandedSale.receipt_number}</p>
                    <p className="rpt-expanded-meta">
                      Cashier: <strong style={{ color: "var(--cyan)" }}>{expandedSale.cashier?.full_name ?? "—"}</strong>
                      &nbsp;·&nbsp;{new Date(expandedSale.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button className="rpt-expanded-close" onClick={() => setExpanded(null)} title="Close">✕</button>
                </div>

                {!items ? (
                  <p style={{ color: "var(--text3)", fontSize: "13px" }}>Loading...</p>
                ) : (
                  <>
                    {/* Desktop: table */}
                    <table className="items-table items-table-desktop">
                      <thead><tr><th>Product</th><th>Qty</th><th>Unit Price</th><th>Subtotal</th></tr></thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.id}>
                            <td>{item.product_name}</td>
                            <td style={{ color: "var(--cyan)", fontWeight: "bold" }}>{item.quantity}</td>
                            <td>{money(item.unit_price)}</td>
                            <td style={{ color: "var(--green)", fontWeight: "bold" }}>{money(item.line_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Mobile: stacked label:value cards — same pattern as
                        Warehouse's Products Received fix, since a 4-column
                        table squeezed into a narrow width is a bad
                        interaction regardless of sizing. */}
                    <div className="items-cards-mobile">
                      {items.map((item) => (
                        <div key={item.id} className="items-card">
                          <p className="items-card-name">{item.product_name}</p>
                          <div className="items-card-row">
                            <span className="items-card-label">Qty</span>
                            <span className="items-card-value" style={{ color: "var(--cyan)" }}>{item.quantity}</span>
                          </div>
                          <div className="items-card-row">
                            <span className="items-card-label">Unit Price</span>
                            <span className="items-card-value">{money(item.unit_price)}</span>
                          </div>
                          <div className="items-card-row">
                            <span className="items-card-label">Subtotal</span>
                            <span className="items-card-value" style={{ color: "var(--green)" }}>{money(item.line_total)}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Totals — shared markup at every width, not a table */}
                    <div className="rpt-expanded-totals">
                      {vatEnabled && (
                        <>
                          <div className="rpt-expanded-total-row">
                            <span>Subtotal</span>
                            <span>{money(subtotal)}</span>
                          </div>
                          <div className="rpt-expanded-total-row">
                            <span>VAT Rate</span>
                            <span>{Number.isFinite(vatRate) ? `${vatRate}%` : "—"}</span>
                          </div>
                          <div className="rpt-expanded-total-row">
                            <span>VAT Amount</span>
                            <span>{money(taxAmount)}</span>
                          </div>
                        </>
                      )}
                      <div className="rpt-expanded-total-row rpt-expanded-total-final">
                        <span>{vatEnabled ? "Total (Incl. VAT)" : "Total"}</span>
                        <span>{money(totalInclVat)}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
      </div>
    </div>
  );
}